import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import AccessTime from "@mui/icons-material/AccessTime";
import PaymentsIcon from "@mui/icons-material/Payments";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../utils/api";

/**
 * Dashboard card that lists bonds currently marked as Unpaid by Liberty.
 * Used on every home/dashboard page (internal user, admin, law firm, PG).
 *
 * Props:
 *   lawFirmId (number | null): When provided, the backend scopes the list
 *     to that firm. Omit for internal/admin/super views to see all unpaid
 *     bonds across the org.
 *   limit (number): Max rows to fetch from the API (default 25000, server-capped).
 */
const UnpaidBondsCard = ({ lawFirmId = null, limit = 25000 }) => {
  const navigate = useNavigate();
  const userdetails = useSelector((state) => state.auth.user);
  const role = useSelector((state) => state.auth.role);
  const canOverridePaymentStatus =
    role === "internal_user" || role === "super_user";

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalUnpaid, setTotalUnpaid] = useState(null);
  const [returnedCount, setReturnedCount] = useState(null);
  const [error, setError] = useState(null);

  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedBond, setSelectedBond] = useState(null);
  const [overrideStatus, setOverrideStatus] = useState("paid");
  const [overrideReason, setOverrideReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadUnpaid = useCallback(async () => {
    const isLawFirmRole =
      role === "law_firm_user" || role === "law_firm_admin";
    if (isLawFirmRole && !lawFirmId) {
      setRows([]);
      setTotalUnpaid(null);
      setReturnedCount(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = [];
      if (lawFirmId) params.push(`law_firm_id=${lawFirmId}`);
      if (limit) params.push(`limit=${limit}`);
      const qs = params.length ? `?${params.join("&")}` : "";
      const data = await apiClient(`/unpaid-bonds/${qs}`, "GET");
      setRows(Array.isArray(data?.results) ? data.results : []);
      setTotalUnpaid(typeof data?.count === "number" ? data.count : null);
      setReturnedCount(
        typeof data?.returned === "number"
          ? data.returned
          : Array.isArray(data?.results)
            ? data.results.length
            : null
      );
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load");
      setRows([]);
      setTotalUnpaid(null);
      setReturnedCount(null);
    } finally {
      setLoading(false);
    }
  }, [lawFirmId, limit, role]);

  useEffect(() => {
    loadUnpaid();
  }, [loadUnpaid]);

  const openBond = (row) => {
    const type = (row.bond_type || "").toLowerCase().replace(" ", "-");
    if (!type) return;
    window.open(`/vermost-${type}-bond-update/${row.bond_id}`, "_blank");
  };

  const handleOpenOverride = (row) => {
    setSelectedBond(row);
    setOverrideStatus("paid");
    setOverrideReason("");
    setOverrideDialogOpen(true);
  };

  const handleOverrideConfirm = async () => {
    if (!selectedBond) return;
    const overriddenBy =
      userdetails?.first_name && userdetails?.last_name
        ? `${userdetails.first_name} ${userdetails.last_name}`
        : userdetails?.email || "Unknown";
    setIsSubmitting(true);
    try {
      await apiClient("/liberty-payment-status/override/", "POST", {
        bond_id: selectedBond.bond_id,
        new_status: overrideStatus,
        reason: overrideReason,
        overridden_by: overriddenBy,
      });
      toast.success("Payment status updated. Notification sent.");
      setOverrideDialogOpen(false);
      setSelectedBond(null);
      await loadUnpaid();
    } catch (err) {
      console.error("Payment override error:", err);
      toast.error(err?.response?.data?.detail || err?.message || "Failed to update payment status");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPaymentStatus = (value) => {
    const v = (value || "").toLowerCase();
    if (v === "paid") return "Paid";
    if (v === "unpaid") return "Unpaid";
    if (v === "na") return "N/A";
    return "Unknown";
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 1,
        background: (theme) =>
          theme.palette.mode === "light"
            ? "linear-gradient(135deg, #fff5f5 0%, #ffe9e9 50%, #fff5f5 100%)"
            : theme.palette.background.paper,
        border: "1px solid rgba(220, 53, 69, 0.2)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentsIcon sx={{ color: "#dc3545", fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.25, color: "#333333" }}>
              Unpaid Bonds
            </Typography>
            <Chip
              size="small"
              color="error"
              label={totalUnpaid != null ? totalUnpaid : rows.length}
            />
          </Box>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Bonds Liberty currently reports as unpaid
          </Typography>
        </Box>
        {loading && <CircularProgress size={18} />}
      </Stack>

      {error ? (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      ) : rows.length === 0 && !loading ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No unpaid bonds right now.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxHeight: 320,
            minHeight: 0,
            flex: 1,
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          {rows.map((row) => (
            <Box
              key={row.bond_id}
              sx={{
                p: 1.25,
                borderRadius: 1,
                bgcolor: (theme) => (theme.palette.mode === "light" ? "#ffffff" : "background.default"),
                border: "1px solid rgba(220, 53, 69, 0.15)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>
                  {row.bond_number || "—"}{" "}
                  <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
                    ({row.bond_type}{row.bond_sub_category ? ` / ${row.bond_sub_category}` : ""})
                  </Box>
                </Typography>
                {row.name ? (
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                    {row.name}
                  </Typography>
                ) : null}
                <Box mt={0.5} display="flex" alignItems="center" gap={1}>
                  {row.law_firm_name ? (
                    <Chip size="small" variant="outlined" label={row.law_firm_name} />
                  ) : null}
                  {row.payment_status_updated_at ? (
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
                      <AccessTime sx={{ fontSize: 13, color: "text.disabled" }} />
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {dayjs(row.payment_status_updated_at).fromNow ? dayjs(row.payment_status_updated_at).format("MMM D, h:mm A") : ""}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ alignSelf: "flex-end", flexShrink: 0 }}>
                {canOverridePaymentStatus ? (
                  <Tooltip title="Override Payment Status">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenOverride(row)}
                      sx={{ color: "#dc3545" }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                <Button
                  size="small"
                  variant="text"
                  onClick={() => openBond(row)}
                  sx={{ textTransform: "none", color: "#2563eb", whiteSpace: "nowrap" }}
                >
                  View Bond
                </Button>
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      {totalUnpaid != null &&
      returnedCount != null &&
      returnedCount < totalUnpaid ? (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
          Showing {returnedCount} of {totalUnpaid} unpaid bonds (list capped by server limit).
        </Typography>
      ) : null}

      <Divider sx={{ my: 1.25, borderColor: "rgba(220, 53, 69, 0.12)" }} />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          size="small"
          variant="text"
          startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={() => navigate("/vermost_bond_archives")}
          sx={{ textTransform: "none", color: "#6b7280", fontSize: "0.8rem" }}
        >
          View in Bond Archives
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={loadUnpaid}
          disabled={loading}
          sx={{ textTransform: "none", color: "#dc3545", borderColor: "rgba(220, 53, 69, 0.3)" }}
        >
          Refresh
        </Button>
      </Box>

      {/* Payment Status Override Dialog — matches Bond Archives style */}
      <Dialog
        open={overrideDialogOpen}
        onClose={() => !isSubmitting && (setOverrideDialogOpen(false), setSelectedBond(null))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Override Payment Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Override the payment status returned by Liberty for this bond.
            Use this only when you have verified the correct status outside of Liberty (e.g. Agency Bill reconciliation).
          </Typography>
          {selectedBond && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Bond</Typography>
              <Typography variant="body2">
                <b>{selectedBond.bond_number || "—"}</b>
                {" "}- {selectedBond.bond_type}{selectedBond.bond_sub_category ? ` / ${selectedBond.bond_sub_category}` : ""}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                Current: <b>{formatPaymentStatus(selectedBond.payment_status || "unpaid")}</b>
              </Typography>
            </Box>
          )}
          <TextField
            select
            label="New Status"
            fullWidth
            value={overrideStatus}
            onChange={(e) => setOverrideStatus(e.target.value)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="unpaid">Unpaid</MenuItem>
            <MenuItem value="na">N/A (Not Applicable)</MenuItem>
          </TextField>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            rows={3}
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="e.g. Agency Bill - paid by check ref #12345"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => { setOverrideDialogOpen(false); setSelectedBond(null); }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleOverrideConfirm}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UnpaidBondsCard;
